// CommandPalette — Ctrl/Cmd+K action launcher. A discoverable list of app
// actions (also a discovery surface for keyboard shortcuts users never find).
// The host (Shell) supplies the currently-available commands; this component
// fuzzy-filters, keyboard-navigates, and runs the chosen one.
//
// Props:
//   open        bool
//   commands    array of { label, group?, shortcut?, run }
//   onClose()
import { createSignal, createEffect, createMemo, For, Show } from 'solid-js';
import Modal from './Modal.jsx';
import { rankCommands } from '../lib/quickopen.js';

export default function CommandPalette(props) {
  const [query, setQuery] = createSignal('');
  const [sel, setSel] = createSignal(0);
  let inputRef, listRef;

  createEffect(() => {
    if (props.open) { setQuery(''); setSel(0); queueMicrotask(() => inputRef?.focus()); }
  });

  const results = createMemo(() => rankCommands(props.commands || [], query(), 50));
  createEffect(() => { results(); setSel((s) => Math.min(s, Math.max(0, results().length - 1))); });

  const run = (cmd) => { if (cmd) { props.onClose?.(); cmd.run?.(); } };

  const onKey = (e) => {
    const n = results().length;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => (n ? (s + 1) % n : 0)); scrollSel(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => (n ? (s - 1 + n) % n : 0)); scrollSel(); }
    else if (e.key === 'Enter') { e.preventDefault(); run(results()[sel()]); }
    else if (e.key === 'Escape') { e.preventDefault(); props.onClose?.(); }
  };
  const scrollSel = () => queueMicrotask(() =>
    listRef?.querySelector('.quickopen-row.active')?.scrollIntoView({ block: 'nearest' }));

  return (
    <Show when={props.open}>
      <Modal open={true} title="Command Palette" onClose={props.onClose} cardClass="quickopen-modal">
        <div class="quickopen">
          <input
            ref={inputRef}
            class="input quickopen-input"
            placeholder="Type a command…"
            value={query()}
            onInput={(e) => { setQuery(e.currentTarget.value); setSel(0); }}
            onKeyDown={onKey}
          />
          <div class="quickopen-list" ref={listRef}>
            <Show when={results().length > 0} fallback={<div class="muted" style="padding:14px;">No matching commands.</div>}>
              <For each={results()}>
                {(c, i) => (
                  <div class="quickopen-row" classList={{ active: i() === sel() }}
                       onMouseEnter={() => setSel(i())}
                       onClick={() => run(c)}>
                    <Show when={c.group}><span class="cmdk-group">{c.group}</span></Show>
                    <span class="quickopen-name">{c.label}</span>
                    <span class="spacer" />
                    <Show when={c.shortcut}><span class="cmdk-shortcut mono">{c.shortcut}</span></Show>
                  </div>
                )}
              </For>
            </Show>
          </div>
          <div class="quickopen-foot muted">
            <span><b>↑↓</b> navigate · <b>Enter</b> run · <b>Esc</b> close</span>
            <span class="spacer" />
            <span>{results().length}</span>
          </div>
        </div>
      </Modal>
    </Show>
  );
}
